import { num, optionalNum } from '@/features/home/model'
import { parseUTCDate } from '@/lib/utils'

import type { CustomerInsights } from './api'

type Bucket = CustomerInsights['summary']['buckets'][number]

const monthShort = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' })
const monthShortYear = new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' })

/** "2026-06" → first of that month, UTC. */
export function monthDate(month: string) {
  return new Date(`${month}-01T00:00:00Z`)
}

function nextMonth(month: string) {
  const date = monthDate(month)
  date.setUTCMonth(date.getUTCMonth() + 1)
  return date.toISOString().slice(0, 7)
}

export function localMonth(value: string) {
  const date = parseUTCDate(value)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

export interface MonthPoint {
  month: string
  label: string
  revenue: number
  net: number
  profit: number
  margin: number | null
  orders: number
  projects: number
  averageOrder: number | null
}

/** One point per month from the first order or project through this month. */
export function monthPoints(buckets: Bucket[], projectDates: string[], today = new Date()): MonthPoint[] {
  const byMonth = new Map(buckets.map((bucket) => [bucket.start.slice(0, 7), bucket]))
  const projects = new Map<string, number>()
  for (const created of projectDates) {
    const month = localMonth(created)
    projects.set(month, (projects.get(month) ?? 0) + 1)
  }
  const months = [...byMonth.keys(), ...projects.keys()].sort()
  const first = months[0]
  if (!first) return []
  const last = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  const multiYear = first.slice(0, 4) !== last.slice(0, 4)
  const points: MonthPoint[] = []
  for (let month = first; month <= last; month = nextMonth(month)) {
    const bucket = byMonth.get(month)
    points.push({
      month,
      label: (multiYear ? monthShortYear : monthShort).format(monthDate(month)),
      revenue: num(bucket?.revenue),
      net: num(bucket?.net),
      profit: num(bucket?.profit),
      margin: bucket?.orders ? optionalNum(bucket.margin) : null,
      orders: bucket?.orders ?? 0,
      projects: projects.get(month) ?? 0,
      averageOrder: optionalNum(bucket?.average_order),
    })
  }
  return points
}
